import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Send, Trash2, Loader2 } from 'lucide-react';
import { useAppStore, useChatStore } from '../store';
import { generateId } from '@jrd/core';

const JACK2_SYSTEM_CONTEXT = `You are Jack2, an AI assistant built into JRD Fuel & Auto operations platform. You help users with:
- Fuel pricing and margin analysis
- Competitor monitoring
- Accounting and QBO integration
- Auto service shop operations
- Document processing

Be direct, concise, and helpful. Use industry terminology. Never be verbose. Match the user's energy.`;

export function Jack2Chat() {
  const { setJack2Open } = useAppStore();
  const { messages, addMessage, clearMessages, isLoading, setIsLoading } = useChatStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input on open
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: generateId(),
      role: 'user' as const,
      content: input.trim(),
      timestamp: new Date(),
    };

    addMessage(userMessage);
    setInput('');
    setIsLoading(true);

    try {
      // Call Claude API via proxy worker
      const response = await fetch('https://jack2-api.workers.dev/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: input.trim(),
          context: {
            system: JACK2_SYSTEM_CONTEXT,
            history: messages.slice(-10).map(m => ({
              role: m.role,
              content: m.content,
            })),
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      addMessage({
        id: generateId(),
        role: 'assistant',
        content: data.response || data.message || 'No response',
        timestamp: new Date(),
      });
    } catch (error) {
      console.error('Jack2 error:', error);
      addMessage({
        id: generateId(),
        role: 'assistant',
        content: '> connection error. try again.',
        timestamp: new Date(),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 20, scale: 0.95 }}
      className="fixed bottom-24 right-6 w-96 max-h-[600px] bg-bg-darker border border-text-muted/30 rounded-lg shadow-2xl z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-text-muted/20">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-jrd-green animate-pulse" />
          <span className="text-jrd-orange font-mono text-sm font-bold">JACK2</span>
          <span className="text-text-muted text-xs">// AI Assistant</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={clearMessages}
            className="p-1.5 text-text-muted hover:text-text-secondary transition-colors"
            title="Clear chat"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => setJack2Open(false)}
            className="p-1.5 text-text-muted hover:text-text-secondary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[300px] max-h-[400px] scrollbar-hide">
        {messages.length === 0 ? (
          <div className="text-center text-text-muted text-sm py-8">
            <div className="text-jrd-orange font-mono mb-2">&gt; jack2 ready</div>
            <div>Ask me anything about your operations.</div>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-3 py-2 rounded-lg font-mono text-sm ${
                  message.role === 'user'
                    ? 'bg-jrd-orange/20 text-text-primary border border-jrd-orange/30'
                    : 'bg-bg-card text-text-secondary border border-text-muted/20'
                }`}
              >
                {message.role === 'assistant' && (
                  <span className="text-jrd-green mr-1">jack2 &gt;</span>
                )}
                <span className="whitespace-pre-wrap">{message.content}</span>
              </div>
            </div>
          ))
        )}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-bg-card border border-text-muted/20 px-3 py-2 rounded-lg">
              <span className="text-jrd-green font-mono text-sm">jack2 &gt;</span>
              <span className="text-text-muted ml-1 animate-pulse">processing...</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-text-muted/20">
        <div className="flex items-center gap-2">
          <span className="text-jrd-orange font-mono">&gt;</span>
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="flex-1 bg-transparent border-none outline-none text-text-primary placeholder-text-muted font-mono text-sm"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="p-2 text-jrd-orange hover:bg-jrd-orange/10 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
}
